import { useCallback } from 'react';
import topicsPathwayArray, { topicProgression, unitProgression } from '../infrastructure/topicsPathwayArray';
import { config } from '../constants.js';

const url = config.url.API_URL;

const useLastSkillTracking = (userId) => {

  const trackLastSkill = useCallback(async (skillId, action = 'accessed') => {
    if (!userId || !skillId) return;

    try {
      // Find the skill data from topicsPathwayArray
      const skillData = topicsPathwayArray.find(skill => String(skill.skillId) === String(skillId));
      if (!skillData) return;

      const lastSkillData = {
        lastSkillId: parseInt(skillId),
        lastSkillTimestamp: new Date().toISOString(),
        lastSkillUnit: skillData.unit,
        lastSkillTopic: skillData.topic,
        lastAction: action
      };

      // Store in localStorage for immediate access
      localStorage.setItem('lastSkill', JSON.stringify(lastSkillData));

      // Also store in database via preferences endpoint
      await fetch(`${url}/users/preferences`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(lastSkillData)
      });

      console.log(`🎯 Last skill tracked (${action}):`, skillData.skillDisplayName);
    } catch (error) {
      console.error('Error tracking last skill:', error);
    }
  }, [userId]);

  const getLastSkill = useCallback(async (currentUserId = userId) => {
    try {
      // First try localStorage for immediate access
      const localData = localStorage.getItem('lastSkill');
      if (localData) {
        const lastSkill = JSON.parse(localData);
        
        // Check if data is recent (within 10 days)
        const lastAccessed = new Date(lastSkill.lastSkillTimestamp);
        const daysSince = (new Date() - lastAccessed) / (1000 * 60 * 60 * 24);
        
        if (daysSince <= 10) {
          return lastSkill;
        }
      }

      // Fallback to server data if localStorage is empty or old
      if (!currentUserId) return null;

      const response = await fetch(`${url}/users/fetchUsername?userId=${currentUserId}`, {
        credentials: 'include'
      });
      const userData = await response.json();
      
      if (userData.preferences?.lastSkillTimestamp) {
        const lastAccessed = new Date(userData.preferences.lastSkillTimestamp);
        const daysSince = (new Date() - lastAccessed) / (1000 * 60 * 60 * 24);
        
        if (daysSince <= 10) {
          return {
            lastSkillId: userData.preferences.lastSkillId,
            lastSkillTimestamp: userData.preferences.lastSkillTimestamp,
            lastSkillUnit: userData.preferences.lastSkillUnit,
            lastSkillTopic: userData.preferences.lastSkillTopic,
            lastAction: userData.preferences.lastAction
          };
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting last skill:', error);
      return null;
    }
  }, [userId]);

  // Smart progression helper functions
  const getSkillCompletionStatus = useCallback(async (currentUserId, skillIds) => {
    if (!currentUserId || !skillIds.length) return {};

    try {
      const response = await fetch(`${url}/record/check-grouped-skills-completion/${currentUserId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ skillIds })
      });
      return await response.json();
    } catch (error) {
      console.error('Error getting skill completion status:', error);
      return {};
    }
  }, []);

  const findNextSkillInTopic = useCallback((topicSkills, currentLevel) => {
    const sortedSkills = topicSkills
      .filter(skill => skill.isLive)
      .sort((a, b) => a.level - b.level);
    
    const currentIndex = sortedSkills.findIndex(skill => skill.level === currentLevel);
    return currentIndex >= 0 && currentIndex < sortedSkills.length - 1 
      ? sortedSkills[currentIndex + 1] 
      : null;
  }, []);

  const findFirstIncompleteSkillInTopic = useCallback((topicSkills, completionStatus) => {
    return topicSkills
      .filter(skill => skill.isLive && !completionStatus[skill.skillId])
      .sort((a, b) => a.level - b.level)[0] || null;
  }, []);

  const findNextTopicWithIncompleteSkills = useCallback(async (currentUnit, currentTopic, currentUserId) => {
    try {
      // Get current topic index
      const topicsInUnit = topicProgression[currentUnit] || [];
      const currentTopicIndex = topicsInUnit.indexOf(currentTopic);
      
      // Check remaining topics in current unit
      for (let i = currentTopicIndex + 1; i < topicsInUnit.length; i++) {
        const topic = topicsInUnit[i];
        const topicSkills = topicsPathwayArray.filter(skill => 
          skill.unit === currentUnit && skill.topic === topic && skill.isLive
        );
        
        if (topicSkills.length > 0) {
          const skillIds = topicSkills.map(s => s.skillId);
          const completionStatus = await getSkillCompletionStatus(currentUserId, skillIds);
          const incompleteSkill = findFirstIncompleteSkillInTopic(topicSkills, completionStatus);
          
          if (incompleteSkill) {
            return incompleteSkill;
          }
        }
      }
      
      // Check next units
      const currentUnitIndex = unitProgression.indexOf(currentUnit);
      for (let unitIndex = currentUnitIndex + 1; unitIndex < unitProgression.length; unitIndex++) {
        const unit = unitProgression[unitIndex];
        const unitsTopics = topicProgression[unit] || [];
        
        for (const topic of unitsTopics) {
          const topicSkills = topicsPathwayArray.filter(skill => 
            skill.unit === unit && skill.topic === topic && skill.isLive
          );
          
          if (topicSkills.length > 0) {
            const skillIds = topicSkills.map(s => s.skillId);
            const completionStatus = await getSkillCompletionStatus(currentUserId, skillIds);
            const incompleteSkill = findFirstIncompleteSkillInTopic(topicSkills, completionStatus);
            
            if (incompleteSkill) {
              return incompleteSkill;
            }
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error finding next topic with incomplete skills:', error);
      return null;
    }
  }, [getSkillCompletionStatus, findFirstIncompleteSkillInTopic]);

  const findNextLearningOpportunity = useCallback(async (lastSkillId, currentUserId) => {
    try {
      const lastSkill = topicsPathwayArray.find(skill => skill.skillId === lastSkillId);
      if (!lastSkill) return null;

      // Get all skills in the same topic
      const topicSkills = topicsPathwayArray.filter(skill => 
        skill.unit === lastSkill.unit && 
        skill.topic === lastSkill.topic &&
        skill.isLive
      );

      // Get completion status for current topic
      const skillIds = topicSkills.map(s => s.skillId);
      const completionStatus = await getSkillCompletionStatus(currentUserId, skillIds);

      // Try to find next skill in current topic
      const nextSkillInTopic = findNextSkillInTopic(topicSkills, lastSkill.level);
      if (nextSkillInTopic && !completionStatus[nextSkillInTopic.skillId]) {
        return nextSkillInTopic;
      }

      // Try to find any incomplete skill in current topic
      const incompleteInTopic = findFirstIncompleteSkillInTopic(topicSkills, completionStatus);
      if (incompleteInTopic) {
        return incompleteInTopic;
      }

      // Find next topic with incomplete skills
      return await findNextTopicWithIncompleteSkills(lastSkill.unit, lastSkill.topic, currentUserId);
    } catch (error) {
      console.error('Error finding next learning opportunity:', error);
      return null;
    }
  }, [getSkillCompletionStatus, findNextSkillInTopic, findFirstIncompleteSkillInTopic, findNextTopicWithIncompleteSkills]);

  return { trackLastSkill, getLastSkill, findNextLearningOpportunity };
};

export default useLastSkillTracking;